import { Breakpoints } from '@client/style/Variables';
import { TextStyle } from '@client/style/TextStyles';
import styled from 'styled-components';
import Functions from '@client/style/Functions';
import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { SharedButton } from './Buttons';

const SquareButtonStyle = styled(SharedButton).attrs(props => ({
	...props,
	highContrast: true,
}))`
	overflow: hidden;
	height: max-content;

	${TextStyle.weight('bold')}

	padding: ${props => (props.size === 'small' ? '9px 16px' : '14px 20px')};
	${props => (props.size === 'small' ? TextStyle.size('xlarge') : TextStyle.size('p6'))}

	${Functions.breakpoint(Breakpoints.mobile)} {
		padding: ${props => (props.size === 'small' ? '14px 22px' : '20px 26px')};
		${props => (props.size === 'small' ? TextStyle.size('p5') : TextStyle.size('p4'))}
	}
	--shine: 0%;

	&::after {
		content: '';
		position: absolute;
		top: -1px;
		left: -100%;
		bottom: -1px;
		width: 90%;
		background: white;
		opacity: 0.5;
		transform: skewX(-20deg) translateX(var(--shine));
	}
`;

export const SquareButton = ({ onPointerEnter, ...props }: React.ComponentProps<typeof SquareButtonStyle>) => {
	const buttonRef = useRef<HTMLButtonElement>(null);
	const animationRef = useRef<gsap.core.Timeline>(undefined);

	const internalPointerEnter = (ev: React.PointerEvent<HTMLButtonElement>) => {
		animationRef.current?.restart();
		onPointerEnter?.(ev);
	};

	useEffect(() => {
		const duration = 0.6;

		const timeline = gsap.timeline({
			scrollTrigger: {
				trigger: buttonRef?.current,
				toggleActions: 'play reset play reset',
				start: 'top 80%',
				end: 'bottom 20%',
			},
		});

		if (buttonRef?.current) {
			timeline.to(
				buttonRef?.current,
				{
					keyframes: {
						scale: [1, 1.1, 1],
						easeEach: 'back(1.3).out',
					},
					duration: duration,
					ease: 'none',
				},
				0
			);

			timeline.to(
				buttonRef?.current,
				{
					keyframes: {
						'--shine': ['0%', '100%', '240%'],
						easeEach: 'power1.out',
					},
					duration: duration,
					ease: 'none',
				},
				0
			);
		}

		animationRef.current = timeline;

		return () => {
			animationRef.current?.kill();
		};
	}, [buttonRef?.current]);

	return <SquareButtonStyle {...props} ref={buttonRef} onPointerEnter={internalPointerEnter} />;
};
